/**
 * The football field the public site is drawn on.
 *
 * Everything here is vector: CSS gradients, 3D transforms and inline SVG. No
 * raster art, no external assets (CSP is `self` only).
 *
 *   - <Football>        the ball glyph (inline SVG, laces and stripes)
 *   - <HeroStadium>     the night-game hero backdrop: perspective turf with
 *                       mowing stripes, yard lines, hashes and numbers, light
 *                       banks with bloom, grain, and the kick that sails
 *                       through the uprights on load
 *   - <YardLine>        a top-down chalk yard line with field numbers, the
 *                       divider between "downs" on the scrolling field
 *   - <DriveTracker>    the broadcast-style down & distance bug and the drive
 *                       chart that follows the scroll (ball + line to gain)
 *   - useDriveSections  marks sections as they come into view (fallback for
 *                       browsers without CSS scroll-driven animations)
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

// --------------------------------------------------------------- the ball

export function Football({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 120 72"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 36 C 22 4, 98 4, 116 36 C 98 68, 22 68, 4 36 Z"
        fill="#8a4019"
        stroke="#2a1205"
        strokeWidth="2"
      />
      <path
        d="M10 42 C 30 64, 90 64, 110 42 C 92 58, 28 58, 10 42 Z"
        fill="#4a1d09"
        opacity="0.7"
      />
      <ellipse cx="44" cy="22" rx="22" ry="6" fill="#d98a4f" opacity="0.45" />
      <path d="M22 17 C 26 30, 26 42, 22 55" stroke="#f4efe6" strokeWidth="4" fill="none" />
      <path d="M98 17 C 94 30, 94 42, 98 55" stroke="#f4efe6" strokeWidth="4" fill="none" />
      <path d="M40 30 H 80" stroke="#f4efe6" strokeWidth="3.5" strokeLinecap="round" />
      {[46, 53, 60, 67, 74].map((x) => (
        <path
          key={x}
          d={`M${x} 25.5 V 34.5`}
          stroke="#f4efe6"
          strokeWidth="3"
          strokeLinecap="round"
        />
      ))}
      <path
        d="M14 30 C 34 12, 86 12, 106 30"
        stroke="#ffffff"
        strokeOpacity="0.18"
        strokeWidth="4"
        fill="none"
      />
    </svg>
  );
}

// ------------------------------------------------------------ hero stadium

/** Field numbers painted on the perspective turf, near (bottom) to far. */
const HERO_YARD_NUMBERS = ['10', '20', '30', '40', '50', '40', '30'];

export function HeroStadium(): JSX.Element {
  return (
    <div className="stadium" aria-hidden="true">
      <div className="stadium__sky" />
      <div className="stadium__lights stadium__lights--left">
        <span />
      </div>
      <div className="stadium__lights stadium__lights--right">
        <span />
      </div>
      <div className="stadium__stands" />
      <div className="stadium__turf-wrap">
        <div className="stadium__turf">
          <div className="stadium__hashes" />
          <div className="stadium__endzone" />
          {HERO_YARD_NUMBERS.map((number, index) => (
            <span
              className="stadium__num"
              key={`${number}-${String(index)}`}
              style={{ '--yd': (index + 1) * 10 } as CSSProperties}
            >
              <b>{number}</b>
              <b>{number}</b>
            </span>
          ))}
        </div>
      </div>
      <div className="stadium__haze" />
      <div className="stadium__grain" />
    </div>
  );
}

/**
 * The goal post. Its two uprights and the crossbar frame whatever is passed
 * as children (the hero headline); the gooseneck runs down behind the CTAs.
 */
export function GoalPost({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="goalpost">
      <span className="goalpost__upright goalpost__upright--left" aria-hidden="true" />
      <span className="goalpost__upright goalpost__upright--right" aria-hidden="true" />
      {/* The kick: from the bottom of the hero, end over end, through the
          uprights and away. Hidden under prefers-reduced-motion. */}
      <span className="kick" aria-hidden="true">
        <span className="kick__flight">
          <Football className="kick__ball" />
        </span>
      </span>
      <div className="goalpost__frame">{children}</div>
      <span className="goalpost__crossbar" aria-hidden="true" />
      <span className="goalpost__neck" aria-hidden="true" />
    </div>
  );
}

// ---------------------------------------------------------- top-down field

/**
 * A chalk yard line across the scrolling field. `yard` counts from our own
 * goal line (0–100); the painted number is the distance to the nearer goal.
 */
export function YardLine({ yard }: { yard: number }): JSX.Element {
  const isGoal = yard >= 100 || yard <= 0;
  const painted = isGoal ? '' : String(yard > 50 ? 100 - yard : yard);
  // The arrow beside a field number points at the nearer goal line.
  const arrow = yard === 50 || isGoal ? 'none' : yard < 50 ? 'back' : 'ahead';
  return (
    <div
      className={`yardline${isGoal ? ' yardline--goal' : ''}`}
      data-arrow={arrow}
      aria-hidden="true"
    >
      <span className="yardline__num yardline__num--left">{painted}</span>
      <span className="yardline__chalk" />
      <span className="yardline__num yardline__num--right">{painted}</span>
    </div>
  );
}

// ------------------------------------------------------------ drive chart

export interface DriveMarker {
  id: string;
  down: string;
  spot: string;
  yard: number;
  lineToGain: number;
}

const START: DriveMarker = {
  id: 'kickoff',
  down: 'Touchback',
  spot: 'Own 25',
  yard: 25,
  lineToGain: 35,
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Follows the scroll down the page: which drive stop is at the line of
 * scrimmage (viewport middle) and where the ball is between stops. Writes the
 * ball position as CSS custom properties so the browser does the drawing.
 */
export function DriveTracker({ markers }: { markers: readonly DriveMarker[] }): JSX.Element {
  const railRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState<DriveMarker>(START);

  useEffect(() => {
    const rail = railRef.current;
    if (rail === null) return undefined;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const middle = window.innerHeight * 0.55;
      const stops = markers
        .map((marker) => {
          const element = document.getElementById(marker.id);
          return element === null ? null : { marker, top: element.getBoundingClientRect().top };
        })
        .filter((entry): entry is { marker: DriveMarker; top: number } => entry !== null);
      let yard = START.yard;
      let active = START;
      for (let index = 0; index < stops.length; index += 1) {
        const stop = stops[index];
        if (stop.top > middle) {
          // Ball travels from the previous stop toward this one.
          const previous = index === 0 ? { marker: START, top: 0 } : stops[index - 1];
          const span = Math.max(1, stop.top - previous.top);
          const t = Math.min(1, Math.max(0, (middle - previous.top) / span));
          yard = previous.marker.yard + (stop.marker.yard - previous.marker.yard) * t;
          break;
        }
        active = stop.marker;
        yard = stop.marker.yard;
      }
      const nav = document.querySelector<HTMLElement>('.marketing__nav');
      rail.style.setProperty('--rail-top', `${String(nav?.offsetHeight ?? 64)}px`);
      rail.style.setProperty('--ball-yard', yard.toFixed(2));
      rail.style.setProperty('--gain-yard', String(active.lineToGain));
      setCurrent((previous) => (previous.id === active.id ? previous : active));
    };
    const schedule = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [markers]);

  const isTouchdown = current.yard >= 100;
  return (
    <div
      className="drive-rail"
      aria-hidden="true"
      ref={railRef}
      data-touchdown={isTouchdown ? 'yes' : 'no'}
      style={{ '--ball-yard': START.yard, '--gain-yard': START.lineToGain } as CSSProperties}
    >
      <div className="drive-rail__bug">
        <span className="drive-rail__down">{current.down}</span>
        <span className="drive-rail__spot">{current.spot}</span>
      </div>
      <div className="drive-rail__field">
        <span className="drive-rail__endzone drive-rail__endzone--own" />
        <span className="drive-rail__endzone drive-rail__endzone--opp" />
        <span className="drive-rail__gain" />
        <span className="drive-rail__los" />
        <Football className="drive-rail__ball" />
      </div>
    </div>
  );
}

/**
 * Adds `is-inview` to every `[data-reveal]` element once it reaches the
 * viewport. Browsers with CSS scroll-driven animations animate the reveal
 * themselves; this keeps the play-by-play mockups paused until they are seen
 * and is the reveal fallback everywhere else.
 */
export function useRevealOnScroll(): void {
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      for (const target of targets) target.classList.add('is-inview');
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.classList.toggle('is-inview', entry.isIntersecting);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, []);
}
